"use client";

/* eslint-disable @next/next/no-img-element -- Preserve the original SVG fragment geometry. */
import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import styles from "./hero-panels.module.scss";

function Icon({ file, className }: { file: string; className?: string }) {
  return (
    <img
      src={`/ai-features/hero-panels/${file}`}
      alt=""
      className={className}
    />
  );
}

function Actions({ start = false }: { start?: boolean }) {
  return (
    <div className={styles.actions}>
      <div className={styles.backAction}>
        <Icon
          file={
            start ? "d-score-chevron-left.svg" : "d-morning-chevron-left.svg"
          }
        />
        <span>Go back</span>
      </div>
      <div className={start ? styles.startAction : styles.continueAction}>
        <span>{start ? "Start trading" : "Continue"}</span>
        <Icon
          file={
            start ? "d-score-chevron-right.svg" : "d-morning-chevron-right.svg"
          }
        />
      </div>
    </div>
  );
}

function ScorePanel() {
  const metrics = [
    ["45", "Body", "d-score-group2085665518.svg"],
    ["78", "Mind", "d-score-group2085665517.svg"],
    ["54", "Moment", "d-score-group2085665516.svg"],
    ["33", "Preparation", "d-score-group2085665515.svg"],
  ];
  return (
    <div
      className={`${styles.panel} ${styles.score}`}
      data-hero-float
      data-position="score"
      aria-hidden="true"
    >
      <div className={`${styles.card} ${styles.scoreCard}`}>
        <div className={styles.scoreMain}>
          <div className={styles.scoreTop}>
            <div className={styles.scoreHeader}>
              <div className={styles.user}>
                <span>F</span>
                <p>Felipe Terra</p>
              </div>
              <div className={styles.scoreTitle}>
                <p>Your morning brief is ready!</p>
                <p>Here is your daily score</p>
              </div>
            </div>
            <div className={styles.gauge}>
              <div className={styles.gaugeTrack}>
                <Icon file="d-score-ellipse138.svg" />
              </div>
              <div className={styles.gaugeFill} data-hero-motion="reveal">
                <Icon file="d-score-ellipse137.svg" />
              </div>
              <div className={styles.gaugeValue}>67</div>
              <div className={styles.gaugeLabel}>Good</div>
              <div className={styles.gaugeMin}>00</div>
              <div className={styles.gaugeMax}>100</div>
              <span className={styles.gaugeNeedle} />
            </div>
          </div>
          <div className={styles.metrics}>
            {metrics.map(([value, label, file]) => (
              <div className={styles.metric} key={label} data-hero-motion="rise">
                <Icon file={file} />
                <div>
                  <p>{value}</p>
                  <p>{label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <Actions start />
      </div>
    </div>
  );
}

function RiskPanel() {
  return (
    <div
      className={`${styles.panel} ${styles.morning}`}
      data-hero-float
      data-position="morning"
      aria-hidden="true"
    >
      <div className={`${styles.card} ${styles.riskCard}`}>
        <div className={styles.riskHeader}>
          <div>
            <p>Morning Brief</p>
            <p>Answer all questions for your mental score</p>
          </div>
          <div className={styles.progress}>
            <span data-hero-motion="grow" />
          </div>
        </div>
        <div className={styles.riskForm}>
          <p className={styles.riskTitle}>Today's Risk Limits</p>
          <div className={styles.fields}>
            {[
              ["Daily loss limit:", "$ value"],
              ["Max trades today:", "How many"],
              ["Additional goal", "Insert here"],
            ].map(([label, value]) => (
              <div className={styles.field} key={label} data-hero-motion="rise">
                <p>{label}</p>
                <div>
                  <p>{value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <Actions />
      </div>
    </div>
  );
}

function WrappedPanel() {
  return (
    <div
      className={`${styles.panel} ${styles.wrapped}`}
      data-hero-float
      data-position="wrapped"
      aria-hidden="true"
    >
      <div className={styles.story}>
        <div className={styles.storyBackdrop} />
        <div className={styles.storyTexture}>
          <Icon file="wrapped-image20.webp" />
        </div>
        <Icon file="d-wrapped-rectangle191.svg" className={styles.storyGlow} />
        <div className={styles.segments}>
          {Array.from({ length: 6 }, (_, i) => (
            <span key={i} />
          ))}
        </div>
        <div className={styles.storyContent}>
          <p className={styles.storyTitle}>Trade of the day</p>
          <div className={styles.deal}>
            <div className={styles.symbol} data-hero-motion="rise">
              <Icon
                file="d-wrapped-ellipse134.svg"
                className={styles.symbolDot}
              />
              <p>BTC / USDT</p>
              <span className={styles.symbolDivider}>
                <Icon file="d-wrapped-line129.svg" />
              </span>
              <span className={styles.side}>
                <p>Long</p>
                <span className={styles.symbolArrow}>
                  <Icon file="d-wrapped-vector243.svg" />
                </span>
              </span>
            </div>
            <div className={styles.profit} data-hero-motion="rise">
              <p>Net profit</p>
              <p className={styles.profitValue}>+$1.240</p>
              <div className={styles.profitBadge}>
                <p>+18,4%</p>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.share}>
          <p>Share wrapped</p>
          <Icon file="d-wrapped-tabler-icon-share2.svg" />
        </div>
      </div>
    </div>
  );
}

export default function HeroPanels() {
  const root = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const node = root.current;
    if (!node || reducedMotion) return;
    const panels = [...node.querySelectorAll<HTMLElement>("[data-hero-float]")];
    const animations = new Map<HTMLElement, Animation[]>();
    const visible = new Set<Element>();
    panels.forEach((panel, index) => {
      const duration = 8400 + index * 350;
      animations.set(
        panel,
        [...panel.querySelectorAll<HTMLElement>("[data-hero-motion]")].map(
          (part, order) => {
            const kind = part.dataset.heroMotion;
            const hidden: Keyframe =
              kind === "grow"
                ? { transform: "scaleX(0.12)" }
                : kind === "reveal"
                  ? { clipPath: "inset(0 100% 0 0)" }
                  : { transform: "translateY(6px)" };
            const shown: Keyframe =
              kind === "grow"
                ? { transform: "scaleX(1)" }
                : kind === "reveal"
                  ? { clipPath: "inset(0 0 0 0)" }
                  : { transform: "translateY(0)" };
            const at = 120 + order * 100;
            const animation = part.animate(
              [
                { ...hidden, opacity: 0, offset: 0 },
                {
                  ...hidden,
                  opacity: 0,
                  offset: at / duration,
                  easing: "cubic-bezier(0.16, 1, 0.3, 1)",
                },
                { ...shown, opacity: 1, offset: (at + 700) / duration },
                { ...shown, opacity: 1, offset: 0.95 },
                { ...shown, opacity: 0, offset: 1 },
              ],
              { duration, iterations: Infinity, fill: "both" },
            );
            animation.pause();
            return animation;
          },
        ),
      );
    });
    const sync = () => {
      animations.forEach((sequence, panel) => {
        const awake = visible.has(panel) && !document.hidden;
        sequence.forEach((animation) => {
          if (awake) animation.play();
          else animation.pause();
        });
        panel.dataset.heroPanelState = awake ? "playing" : "paused";
      });
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        sync();
      },
      { threshold: 0.2 },
    );
    panels.forEach((panel) => observer.observe(panel));
    document.addEventListener("visibilitychange", sync);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      animations.forEach((sequence) =>
        sequence.forEach((animation) => animation.cancel()),
      );
    };
  }, [reducedMotion]);

  return (
    <div ref={root} className={styles.panels}>
      <ScorePanel />
      <RiskPanel />
      <WrappedPanel />
    </div>
  );
}
